import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Database, Package, Plus, Pencil, Trash2, Search, Download, Building2, Tag, Hash, Check, ChevronDown, Factory } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Paper types that use reel-specific fields - these are deprecated, will be empty
const PAPER_TYPES = [];

const StoreDatabase = () => {
  const [activeTab, setActiveTab] = useState("materials");
  
  // Materials state
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [isAddMaterialOpen, setIsAddMaterialOpen] = useState(false);
  const [isEditMaterialOpen, setIsEditMaterialOpen] = useState(false);
  const [deleteMaterialOpen, setDeleteMaterialOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  
  // Vendors state (Dealers)
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [isAddVendorOpen, setIsAddVendorOpen] = useState(false);
  const [isEditVendorOpen, setIsEditVendorOpen] = useState(false);
  const [deleteVendorOpen, setDeleteVendorOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorSearch, setVendorSearch] = useState("");
  
  // Mills state
  const [mills, setMills] = useState([]);
  const [loadingMills, setLoadingMills] = useState(true);
  const [isAddMillOpen, setIsAddMillOpen] = useState(false);
  const [isEditMillOpen, setIsEditMillOpen] = useState(false);
  const [deleteMillOpen, setDeleteMillOpen] = useState(false);
  const [selectedMill, setSelectedMill] = useState(null);
  const [millSearch, setMillSearch] = useState("");
  
  // Product Types state
  const [productTypes, setProductTypes] = useState([]);
  const [loadingProductTypes, setLoadingProductTypes] = useState(true);
  const [isAddPTOpen, setIsAddPTOpen] = useState(false);
  const [isEditPTOpen, setIsEditPTOpen] = useState(false);
  const [deletePTOpen, setDeletePTOpen] = useState(false);
  const [selectedPT, setSelectedPT] = useState(null);
  
  // HSN Codes state
  const [hsnCodes, setHsnCodes] = useState([]);
  const [loadingHSN, setLoadingHSN] = useState(true);
  const [isAddHSNOpen, setIsAddHSNOpen] = useState(false);
  const [isEditHSNOpen, setIsEditHSNOpen] = useState(false);
  const [deleteHSNOpen, setDeleteHSNOpen] = useState(false);
  const [selectedHSN, setSelectedHSN] = useState(null);
  const [hsnToDelete, setHsnToDelete] = useState(null); // Store HSN to delete separately
  
  // Bulk Add states for Materials
  const [isBulkMaterialOpen, setIsBulkMaterialOpen] = useState(false);
  const [bulkMaterialEntries, setBulkMaterialEntries] = useState([{ product_type: "", item_name: "", hsn_code: "", min_stock: "" }]);
  
  // Bulk Add states
  const [isBulkVendorOpen, setIsBulkVendorOpen] = useState(false);
  const [bulkVendorText, setBulkVendorText] = useState("");
  const [isBulkMillOpen, setIsBulkMillOpen] = useState(false);
  const [bulkMillText, setBulkMillText] = useState("");
  const [isBulkPTOpen, setIsBulkPTOpen] = useState(false);
  const [bulkPTText, setBulkPTText] = useState("");
  const [isBulkHSNOpen, setIsBulkHSNOpen] = useState(false);
  const [bulkHSNText, setBulkHSNText] = useState("");
  
  // Material form state
  const [materialForm, setMaterialForm] = useState({
    product_code: "",
    product_type: "",
    item_name: "",
    hsn_code: "",
    reel_size: "",
    reel_size_unit: "inches",
    mill_name: "",
    dealer_name: "",
    buying_price: "",
    min_stock: "",
  });
  
  // Vendor form state (Dealers)
  const [vendorForm, setVendorForm] = useState({
    company_name: "",
    vendor_name: "",
    address: "",
    gstin: "",
    email: "",
    phone: "",
  });
  
  // Mill form state
  const [millForm, setMillForm] = useState({
    name: "",
    address: "",
    contact_person: "",
    phone: "",
    email: "",
    gstin: "",
  });
  
  // Product Type form state
  const [ptForm, setPTForm] = useState({
    name: "",
    is_paper_type: false,
  });
  
  // HSN form state
  const [hsnForm, setHSNForm] = useState({
    code: "",
    description: "",
  });

  // Dropdown states
  const [ptDropdownOpen, setPTDropdownOpen] = useState(false);
  const [hsnDropdownOpen, setHSNDropdownOpen] = useState(false);

  useEffect(() => {
    fetchMaterials();
    fetchVendors();
    fetchMills();
    fetchProductTypes();
    fetchHSNCodes();
  }, []);

  // Fetch functions
  const fetchMaterials = async () => {
    try {
      setLoadingMaterials(true);
      const response = await axios.get(`${BACKEND_URL}/api/materials`);
      setMaterials(response.data);
    } catch (error) {
      toast.error("Failed to fetch materials");
    } finally {
      setLoadingMaterials(false);
    }
  };

  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const response = await axios.get(`${BACKEND_URL}/api/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchMills = async () => {
    try {
      setLoadingMills(true);
      const response = await axios.get(`${BACKEND_URL}/api/mills`);
      setMills(response.data);
    } catch (error) {
      console.error("Error fetching mills:", error);
    } finally {
      setLoadingMills(false);
    }
  };

  const fetchProductTypes = async () => {
    try {
      setLoadingProductTypes(true);
      const response = await axios.get(`${BACKEND_URL}/api/product-types`);
      setProductTypes(response.data);
    } catch (error) {
      console.error("Error fetching product types:", error);
    } finally {
      setLoadingProductTypes(false);
    }
  };

  const fetchHSNCodes = async () => {
    try {
      setLoadingHSN(true);
      // Seed defaults first
      await axios.post(`${BACKEND_URL}/api/hsn-codes/seed-defaults`);
      const response = await axios.get(`${BACKEND_URL}/api/hsn-codes`);
      setHsnCodes(response.data);
    } catch (error) {
      console.error("Error fetching HSN codes:", error);
    } finally {
      setLoadingHSN(false);
    }
  };

  // Check if selected product type is a paper type
  const isPaperType = (productType) => {
    return PAPER_TYPES.some(pt => pt.toLowerCase() === productType?.toLowerCase());
  };

  // Reset forms
  const resetMaterialForm = () => {
    setMaterialForm({
      product_code: "",
      product_type: "",
      item_name: "",
      hsn_code: "",
      reel_size: "",
      reel_size_unit: "inches",
      mill_name: "",
      dealer_name: "",
      buying_price: "",
      min_stock: "",
    });
  };

  const resetVendorForm = () => {
    setVendorForm({ company_name: "", vendor_name: "", address: "", gstin: "", email: "", phone: "" });
  };

  const resetMillForm = () => {
    setMillForm({ name: "", address: "", contact_person: "", phone: "", email: "", gstin: "" });
  };

  const resetPTForm = () => {
    setPTForm({ name: "", is_paper_type: false });
  };

  const resetHSNForm = () => {
    setHSNForm({ code: "", description: "" });
  };

  // ============== MATERIAL CRUD ==============
  const handleAddMaterial = async () => {
    // For non-paper items, use item_name; for paper items, use product_code
    const isNonPaper = !isPaperType(materialForm.product_type);
    
    if (isNonPaper && !materialForm.item_name.trim()) {
      toast.error("Item name is required for non-paper products");
      return;
    }
    
    if (!isNonPaper && !materialForm.product_code.trim()) {
      toast.error("Product code is required for paper products");
      return;
    }

    try {
      const payload = {
        ...materialForm,
        reel_size: materialForm.reel_size ? parseFloat(materialForm.reel_size) : null,
        buying_price: materialForm.buying_price ? parseFloat(materialForm.buying_price) : 0,
        min_stock: materialForm.min_stock ? parseFloat(materialForm.min_stock) : 0,
      };
      
      // Auto-generate product_code for non-paper items if not provided
      if (isNonPaper && !payload.product_code) {
        payload.product_code = `${materialForm.product_type?.replace(/\s+/g, '-').toUpperCase() || 'ITEM'}-${Date.now().toString().slice(-6)}`;
      }

      await axios.post(`${BACKEND_URL}/api/materials`, payload);
      toast.success("Material added successfully");
      setIsAddMaterialOpen(false);
      resetMaterialForm();
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add material");
    }
  };

  const handleEditMaterial = async () => {
    try {
      const payload = {
        ...materialForm,
        reel_size: materialForm.reel_size ? parseFloat(materialForm.reel_size) : null,
        buying_price: materialForm.buying_price ? parseFloat(materialForm.buying_price) : 0,
        min_stock: materialForm.min_stock ? parseFloat(materialForm.min_stock) : 0,
      };

      await axios.put(`${BACKEND_URL}/api/materials/${selectedMaterial.id}`, payload);
      toast.success("Material updated successfully");
      setIsEditMaterialOpen(false);
      resetMaterialForm();
      setSelectedMaterial(null);
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update material");
    }
  };

  const handleDeleteMaterial = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/materials/${selectedMaterial.id}`);
      toast.success("Material deleted successfully");
      setDeleteMaterialOpen(false);
      setSelectedMaterial(null);
      fetchMaterials();
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  const openEditMaterial = (material) => {
    setSelectedMaterial(material);
    setMaterialForm({
      product_code: material.product_code || "",
      product_type: material.product_type || "",
      item_name: material.item_name || "",
      hsn_code: material.hsn_code || "",
      reel_size: material.reel_size?.toString() || "",
      reel_size_unit: material.reel_size_unit || "inches",
      mill_name: material.mill_name || "",
      dealer_name: material.dealer_name || "",
      buying_price: material.buying_price?.toString() || "",
      min_stock: material.min_stock?.toString() || "",
    });
    setIsEditMaterialOpen(true);
  };

  // ============== VENDOR CRUD ==============
  const handleAddVendor = async () => {
    if (!vendorForm.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/vendors`, vendorForm);
      toast.success("Vendor added successfully");
      setIsAddVendorOpen(false);
      resetVendorForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add vendor");
    }
  };

  const handleEditVendor = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/vendors/${selectedVendor.id}`, vendorForm);
      toast.success("Vendor updated successfully");
      setIsEditVendorOpen(false);
      resetVendorForm();
      setSelectedVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update vendor");
    }
  };

  const handleDeleteVendor = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/vendors/${selectedVendor.id}`);
      toast.success("Vendor deleted successfully");
      setDeleteVendorOpen(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error("Failed to delete vendor");
    }
  };

  const openEditVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorForm({
      company_name: vendor.company_name || "",
      vendor_name: vendor.vendor_name || "",
      address: vendor.address || "",
      gstin: vendor.gstin || "",
      email: vendor.email || "",
      phone: vendor.phone || "",
    });
    setIsEditVendorOpen(true);
  };

  // ============== MILL CRUD ==============
  const handleAddMill = async () => {
    if (!millForm.name.trim()) {
      toast.error("Mill name is required");
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/mills`, millForm);
      toast.success("Mill added successfully");
      setIsAddMillOpen(false);
      resetMillForm();
      fetchMills();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add mill");
    }
  };

  const handleEditMill = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/mills/${selectedMill.id}`, millForm);
      toast.success("Mill updated successfully");
      setIsEditMillOpen(false);
      resetMillForm();
      setSelectedMill(null);
      fetchMills();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update mill");
    }
  };

  const handleDeleteMill = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/mills/${selectedMill.id}`);
      toast.success("Mill deleted successfully");
      setDeleteMillOpen(false);
      setSelectedMill(null);
      fetchMills();
    } catch (error) {
      toast.error("Failed to delete mill");
    }
  };

  const openEditMill = (mill) => {
    setSelectedMill(mill);
    setMillForm({
      name: mill.name || "",
      address: mill.address || "",
      contact_person: mill.contact_person || "",
      phone: mill.phone || "",
      email: mill.email || "",
      gstin: mill.gstin || "",
    });
    setIsEditMillOpen(true);
  };

  // Bulk Add Mills Handler
  const handleBulkAddMills = async () => {
    const names = bulkMillText.split('\n').map(n => n.trim()).filter(n => n);
    if (names.length === 0) {
      toast.error("Please enter at least one mill name");
      return;
    }

    try {
      const millsData = names.map(name => ({ name }));
      const response = await axios.post(`${BACKEND_URL}/api/mills/bulk`, millsData);
      toast.success(`Added ${response.data.created} mills`);
      setIsBulkMillOpen(false);
      setBulkMillText("");
      fetchMills();
    } catch (error) {
      toast.error("Failed to add mills");
    }
  };

  // ============== PRODUCT TYPE CRUD ==============
  const handleAddPT = async () => {
    if (!ptForm.name.trim()) {
      toast.error("Product type name is required");
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/product-types`, ptForm);
      toast.success("Product type added successfully");
      setIsAddPTOpen(false);
      resetPTForm();
      fetchProductTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add product type");
    }
  };

  const handleEditPT = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/product-types/${selectedPT.id}`, ptForm);
      toast.success("Product type updated successfully");
      setIsEditPTOpen(false);
      resetPTForm();
      setSelectedPT(null);
      fetchProductTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update product type");
    }
  };

  const handleDeletePT = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/product-types/${selectedPT.id}`);
      toast.success("Product type deleted successfully");
      setDeletePTOpen(false);
      setSelectedPT(null);
      fetchProductTypes();
    } catch (error) {
      toast.error("Failed to delete product type");
    }
  };

  const openEditPT = (pt) => {
    setSelectedPT(pt);
    setPTForm({
      name: pt.name || "",
      is_paper_type: pt.is_paper_type || false,
    });
    setIsEditPTOpen(true);
  };

  // ============== HSN CODE CRUD ==============
  const handleAddHSN = async () => {
    if (!hsnForm.code.trim()) {
      toast.error("HSN code is required");
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/hsn-codes`, hsnForm);
      toast.success("HSN code added successfully");
      setIsAddHSNOpen(false);
      resetHSNForm();
      fetchHSNCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add HSN code");
    }
  };

  const handleEditHSN = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/hsn-codes/${selectedHSN.id}`, hsnForm);
      toast.success("HSN code updated successfully");
      setIsEditHSNOpen(false);
      resetHSNForm();
      setSelectedHSN(null);
      fetchHSNCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update HSN code");
    }
  };

  const handleDeleteHSN = async () => {
    // Use hsnToDelete which was set when dialog opened
    const idToDelete = hsnToDelete?.id;
    if (!idToDelete) {
      toast.error("No HSN code selected for deletion");
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/hsn-codes/${idToDelete}`);
      toast.success("HSN code deleted successfully");
      setDeleteHSNOpen(false);
      setSelectedHSN(null);
      setHsnToDelete(null);
      fetchHSNCodes();
    } catch (error) {
      console.error("Error deleting HSN code:", error);
      toast.error("Failed to delete HSN code");
    }
  };
  
  // Bulk Add Materials Handler
  const handleBulkAddMaterials = async () => {
    const validEntries = bulkMaterialEntries.filter(entry => entry.item_name?.trim() || entry.product_type?.trim());
    if (validEntries.length === 0) {
      toast.error("Please add at least one material");
      return;
    }
    
    let added = 0;
    for (const entry of validEntries) {
      try {
        // For paper types, use item_name as product_code (RM Name)
        // For non-paper types, auto-generate product_code and use item_name as item_name
        const isPaper = isPaperType(entry.product_type);
        const productCode = isPaper 
          ? (entry.item_name || `${entry.product_type?.replace(/\s+/g, '-').toUpperCase() || 'ITEM'}-${Date.now().toString().slice(-6)}-${added}`)
          : `${entry.product_type?.replace(/\s+/g, '-').toUpperCase() || 'ITEM'}-${Date.now().toString().slice(-6)}-${added}`;
        
        await axios.post(`${BACKEND_URL}/api/materials`, {
          product_code: productCode,
          product_type: entry.product_type || "",
          item_name: entry.item_name || "",
          hsn_code: entry.hsn_code || "",
          min_stock: entry.min_stock ? parseFloat(entry.min_stock) : 0,
        });
        added++;
      } catch (error) {
        console.error("Error adding material:", error);
      }
    }
    
    toast.success(`Added ${added} materials`);
    setIsBulkMaterialOpen(false);
    setBulkMaterialEntries([{ product_type: "", item_name: "", hsn_code: "", min_stock: "" }]);
    fetchMaterials();
  };
  
  const addBulkMaterialRow = () => {
    setBulkMaterialEntries([...bulkMaterialEntries, { product_type: "", item_name: "", hsn_code: "", min_stock: "" }]);
  };
  
  const updateBulkMaterialEntry = (index, field, value) => {
    const updated = [...bulkMaterialEntries];
    updated[index][field] = value;
    setBulkMaterialEntries(updated);
  };
  
  const removeBulkMaterialRow = (index) => {
    if (bulkMaterialEntries.length > 1) {
      setBulkMaterialEntries(bulkMaterialEntries.filter((_, i) => i !== index));
    }
  };

  const openEditHSN = (hsn) => {
    setSelectedHSN(hsn);
    setHSNForm({
      code: hsn.code || "",
      description: hsn.description || "",
    });
    setIsEditHSNOpen(true);
  };

  // Bulk Add Handlers
  const handleBulkAddVendors = async () => {
    const lines = bulkVendorText.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      toast.error("Please enter vendor names (one per line)");
      return;
    }
    
    let added = 0;
    for (const line of lines) {
      try {
        await axios.post(`${BACKEND_URL}/api/vendors`, {
          company_name: line.trim(),
          vendor_name: "",
          address: "",
          gstin: ""
        });
        added++;
      } catch (error) {
        console.error("Error adding vendor:", error);
      }
    }
    
    toast.success(`Added ${added} vendors`);
    setIsBulkVendorOpen(false);
    setBulkVendorText("");
    fetchVendors();
  };

  const handleBulkAddPT = async () => {
    const lines = bulkPTText.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      toast.error("Please enter product types (one per line)");
      return;
    }
    
    let added = 0;
    for (const line of lines) {
      try {
        await axios.post(`${BACKEND_URL}/api/product-types`, { name: line.trim() });
        added++;
      } catch (error) {
        console.error("Error adding product type:", error);
      }
    }
    
    toast.success(`Added ${added} product types`);
    setIsBulkPTOpen(false);
    setBulkPTText("");
    fetchProductTypes();
  };

  const handleBulkAddHSN = async () => {
    const lines = bulkHSNText.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      toast.error("Please enter HSN codes (format: CODE - Description, one per line)");
      return;
    }
    
    let added = 0;
    for (const line of lines) {
      const parts = line.split('-').map(p => p.trim());
      const code = parts[0] || "";
      const description = parts.slice(1).join('-').trim() || "";
      
      if (code) {
        try {
          await axios.post(`${BACKEND_URL}/api/hsn-codes`, { code, description });
          added++;
        } catch (error) {
          console.error("Error adding HSN code:", error);
        }
      }
    }
    
    toast.success(`Added ${added} HSN codes`);
    setIsBulkHSNOpen(false);
    setBulkHSNText("");
    fetchHSNCodes();
  };

  // Filter materials
  const filteredMaterials = materials.filter(material => {
    const matchesSearch = 
      material.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.product_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.mill_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.dealer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === "all" || material.product_type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  // Filter vendors
  const filteredVendors = vendors.filter(vendor => {
    if (!vendorSearch) return true;
    const term = vendorSearch.toLowerCase();
    return (
      vendor.company_name?.toLowerCase().includes(term) ||
      vendor.vendor_name?.toLowerCase().includes(term) ||
      vendor.gstin?.toLowerCase().includes(term)
    );
  });

  const filteredMills = mills.filter(mill => {
    if (!millSearch) return true;
    const term = millSearch.toLowerCase();
    return (
      mill.name?.toLowerCase().includes(term) ||
      mill.contact_person?.toLowerCase().includes(term)
    );
  });

  // Get all product types for dropdown (from DB + hardcoded paper types)
  const allProductTypes = [
    ...PAPER_TYPES,
    ...productTypes.map(pt => pt.name).filter(name => !PAPER_TYPES.includes(name))
  ];

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ["Product Code", "Product Type", "Item Name", "HSN Code", "Reel Size (inches)", "Mill Name", "Dealer Name", "Min Stock"];
    const csvContent = [
      headers.join(","),
      ...materials.map(m => [
        m.product_code || "",
        m.product_type || "",
        m.item_name || "",
        m.hsn_code || "",
        m.reel_size_inches || m.reel_size || "",
        `"${m.mill_name || ""}"`,
        `"${m.dealer_name || ""}"`,
        m.min_stock || 0
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `database_materials_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Materials exported to CSV");
  };

  // Format reel size for display
  const formatReelSize = (material) => {
    const sizeInInches = material.reel_size_inches || material.reel_size;
    if (!sizeInInches) return "-";
    return `${sizeInInches.toFixed(2)}"`;
  };

  // Render Material Form Fields based on product type
  const renderMaterialFormFields = (form, setForm, isEdit = false) => {
    const showPaperFields = isPaperType(form.product_type);
    
    return (
      <div className="grid gap-4 py-4">
        {/* Product Type - Always shown first */}
        <div>
          <Label className="text-amber-300">Product Type *</Label>
          <Popover open={ptDropdownOpen} onOpenChange={setPTDropdownOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between bg-black/50 border-amber-500/30 text-white mt-1"
              >
                {form.product_type || "Select or add product type..."}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0 bg-gray-900 border-amber-500/30">
              <Command className="bg-gray-900">
                <CommandInput placeholder="Search product types..." className="bg-gray-900 text-white" />
                <CommandList>
                  <CommandEmpty>No product type found</CommandEmpty>
                  <CommandGroup className="max-h-48 overflow-auto">
                    {allProductTypes.map((pt) => (
                      <CommandItem
                        key={pt}
                        onSelect={() => {
                          setForm({ ...form, product_type: pt });
                          setPTDropdownOpen(false);
                        }}
                        className="text-white hover:bg-amber-500/20 cursor-pointer"
                      >
                        <Check className={cn("mr-2 h-4 w-4", form.product_type === pt ? "opacity-100" : "opacity-0")} />
                        {pt}
                        {PAPER_TYPES.includes(pt) && <span className="ml-2 text-xs text-amber-400">(Paper)</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* HSN Code - Always shown */}
        <div>
          <Label className="text-amber-300">HSN Code</Label>
          <Popover open={hsnDropdownOpen} onOpenChange={setHSNDropdownOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between bg-black/50 border-amber-500/30 text-white mt-1"
              >
                {form.hsn_code || "Select HSN code..."}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0 bg-gray-900 border-amber-500/30">
              <Command className="bg-gray-900">
                <CommandInput placeholder="Search HSN codes..." className="bg-gray-900 text-white" />
                <CommandList>
                  <CommandEmpty>No HSN code found</CommandEmpty>
                  <CommandGroup className="max-h-48 overflow-auto">
                    {hsnCodes.map((hsn) => (
                      <CommandItem
                        key={hsn.id}
                        onSelect={() => {
                          setForm({ ...form, hsn_code: hsn.code });
                          setHSNDropdownOpen(false);
                        }}
                        className="text-white hover:bg-amber-500/20 cursor-pointer"
                      >
                        <Check className={cn("mr-2 h-4 w-4", form.hsn_code === hsn.code ? "opacity-100" : "opacity-0")} />
                        <span className="font-mono">{hsn.code}</span>
                        {hsn.description && <span className="ml-2 text-xs text-gray-400">- {hsn.description}</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Conditional fields based on product type */}
        {showPaperFields ? (
          <>
            {/* Paper-specific fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-amber-300">Product Code (RM Name) *</Label>
                <Input
                  value={form.product_code}
                  onChange={(e) => setForm({ ...form, product_code: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., NS10016"
                />
              </div>
              <div>
                <Label className="text-amber-300">Min Stock</Label>
                <Input
                  type="number"
                  value={form.min_stock}
                  onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., 5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-amber-300">Reel Size</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.reel_size}
                  onChange={(e) => setForm({ ...form, reel_size: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., 28.25"
                />
              </div>
              <div>
                <Label className="text-amber-300">Size Unit</Label>
                <Select value={form.reel_size_unit} onValueChange={(v) => setForm({ ...form, reel_size_unit: v })}>
                  <SelectTrigger className="bg-black/50 border-amber-500/30 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-amber-500/30">
                    <SelectItem value="inches">Inches</SelectItem>
                    <SelectItem value="cm">Centimeters (cm)</SelectItem>
                    <SelectItem value="mm">Millimeters (mm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-amber-300">Mill Name</Label>
                <Input
                  value={form.mill_name}
                  onChange={(e) => setForm({ ...form, mill_name: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., Krishna"
                />
              </div>
              <div>
                <Label className="text-amber-300">Dealer Name</Label>
                <Input
                  value={form.dealer_name}
                  onChange={(e) => setForm({ ...form, dealer_name: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., Suryaans Paper"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-amber-300">Buying Price (₹ per kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.buying_price}
                  onChange={(e) => setForm({ ...form, buying_price: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., 35.50"
                />
              </div>
              <div>
                <Label className="text-amber-300">Min Stock</Label>
                <Input
                  type="number"
                  value={form.min_stock}
                  onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., 5"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Non-paper (Item) fields */}
            <div>
              <Label className="text-amber-300">RM Name / Item Name *</Label>
              <Input
                value={form.item_name}
                onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                className="bg-black/50 border-amber-500/30 text-white mt-1"
                placeholder="e.g., Wooden Pallet, Adhesive, Tape, etc."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-amber-300">Buying Price (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.buying_price}
                  onChange={(e) => setForm({ ...form, buying_price: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., 100.00"
                />
              </div>
              <div>
                <Label className="text-amber-300">Min Stock</Label>
                <Input
                  type="number"
                  value={form.min_stock}
                  onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder="e.g., 5"
                />
              </div>
            </div>
            {/* Hidden product_code for non-paper items - auto-generate */}
            <input type="hidden" value={form.product_code || `ITEM-${Date.now()}`} />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-amber-400 tracking-wider flex items-center gap-3">
            <Database className="h-8 w-8" />
            DATABASE
          </h1>
          <p className="text-gray-400 mt-1">Manage materials, vendors, product types & HSN codes</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 bg-black/60 border border-amber-500/30">
          <TabsTrigger value="materials" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Package className="h-4 w-4 mr-2" />
            Materials
          </TabsTrigger>
          <TabsTrigger value="vendors" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Building2 className="h-4 w-4 mr-2" />
            Dealers
          </TabsTrigger>
          <TabsTrigger value="mills" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Factory className="h-4 w-4 mr-2" />
            Mills
          </TabsTrigger>
          <TabsTrigger value="product-types" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Tag className="h-4 w-4 mr-2" />
            Product Types
          </TabsTrigger>
          <TabsTrigger value="hsn-codes" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Hash className="h-4 w-4 mr-2" />
            HSN Codes
          </TabsTrigger>
        </TabsList>

        {/* ============== MATERIALS TAB ============== */}
        <TabsContent value="materials" className="space-y-4 mt-4">
          <Card className="bg-black/60 border-2 border-amber-500/30">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-amber-400">Raw Materials</CardTitle>
              <div className="flex gap-2">
                <Button
                  onClick={handleExportCSV}
                  variant="outline"
                  size="sm"
                  className="border-gray-600/50 text-cyan-400 hover:bg-cyan-600/20"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button
                  variant="outline"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                  onClick={() => setIsBulkMaterialOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Bulk Add
                </Button>
                <Dialog open={isAddMaterialOpen} onOpenChange={setIsAddMaterialOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={resetMaterialForm}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Material
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-amber-400 text-xl">Add New Material</DialogTitle>
                    </DialogHeader>
                    {renderMaterialFormFields(materialForm, setMaterialForm)}
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddMaterialOpen(false)} className="border-gray-500 text-gray-300">
                        Cancel
                      </Button>
                      <Button onClick={handleAddMaterial} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                        Add Material
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search and Filter */}
              <div className="flex gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search materials..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-black/50 border-amber-500/30 text-white"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[180px] bg-black/50 border-amber-500/30 text-white">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-amber-500/30">
                    <SelectItem value="all">All Types</SelectItem>
                    {allProductTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Materials Table */}
              {loadingMaterials ? (
                <div className="text-center py-8 text-gray-400">Loading materials...</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-amber-500/30 hover:bg-transparent">
                        <TableHead className="text-amber-300">RM Name / Item</TableHead>
                        <TableHead className="text-amber-300">Product Type</TableHead>
                        <TableHead className="text-amber-300">HSN Code</TableHead>
                        <TableHead className="text-amber-300">Reel Size</TableHead>
                        <TableHead className="text-amber-300">Mill - Dealer</TableHead>
                        <TableHead className="text-amber-300 text-center">Min Stock</TableHead>
                        <TableHead className="text-amber-300 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMaterials.map((material) => (
                        <TableRow key={material.id} className="border-amber-500/20 hover:bg-amber-500/10">
                          <TableCell className="font-mono font-bold text-white">
                            {isPaperType(material.product_type) 
                              ? material.product_code 
                              : material.item_name || material.product_code}
                          </TableCell>
                          <TableCell>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs",
                              isPaperType(material.product_type) 
                                ? "bg-amber-500/20 text-amber-400" 
                                : "bg-purple-500/20 text-purple-400"
                            )}>
                              {material.product_type || "-"}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-gray-300">{material.hsn_code || "-"}</TableCell>
                          <TableCell className="text-gray-300">
                            {isPaperType(material.product_type) ? formatReelSize(material) : "-"}
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {isPaperType(material.product_type) 
                              ? `${material.mill_name || "-"} - ${material.dealer_name || "-"}`
                              : "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">
                              {material.min_stock || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditMaterial(material)}
                                className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedMaterial(material);
                                  setDeleteMaterialOpen(true);
                                }}
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Material Dialog */}
          <Dialog open={isEditMaterialOpen} onOpenChange={setIsEditMaterialOpen}>
            <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-amber-400 text-xl">Edit Material</DialogTitle>
              </DialogHeader>
              {renderMaterialFormFields(materialForm, setMaterialForm, true)}
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditMaterialOpen(false)} className="border-gray-500 text-gray-300">
                  Cancel
                </Button>
                <Button onClick={handleEditMaterial} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Material Dialog */}
          <AlertDialog open={deleteMaterialOpen} onOpenChange={setDeleteMaterialOpen}>
            <AlertDialogContent className="bg-gray-900 border-red-500/50">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-400">Delete Material</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-400">
                  Are you sure you want to delete "{selectedMaterial?.product_code || selectedMaterial?.item_name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-gray-500 text-gray-300">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteMaterial} className="bg-red-500 hover:bg-red-600">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* ============== VENDORS TAB ============== */}
        <TabsContent value="vendors" className="space-y-4 mt-4">
          <Card className="bg-black/60 border-2 border-amber-500/30">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-amber-400 flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Vendors
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20" onClick={() => setIsBulkVendorOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Bulk Add
                </Button>
                <Dialog open={isAddVendorOpen} onOpenChange={setIsAddVendorOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={resetVendorForm}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Vendor
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-amber-400 text-xl">Add New Vendor</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div>
                      <Label className="text-amber-300">Vendor Company Name *</Label>
                      <Input
                        value={vendorForm.company_name}
                        onChange={(e) => setVendorForm({ ...vendorForm, company_name: e.target.value })}
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                        placeholder="e.g., ABC Paper Mills Pvt Ltd"
                      />
                    </div>
                    <div>
                      <Label className="text-amber-300">Vendor Name (Optional)</Label>
                      <Input
                        value={vendorForm.vendor_name}
                        onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })}
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                        placeholder="Contact person name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-amber-300">Email</Label>
                        <Input
                          type="email"
                          value={vendorForm.email}
                          onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                          className="bg-black/50 border-amber-500/30 text-white mt-1"
                          placeholder="vendor@example.com"
                        />
                      </div>
                      <div>
                        <Label className="text-amber-300">Phone / WhatsApp</Label>
                        <Input
                          value={vendorForm.phone}
                          onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                          className="bg-black/50 border-amber-500/30 text-white mt-1"
                          placeholder="+91 9876543210"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-amber-300">Address</Label>
                      <Textarea
                        value={vendorForm.address}
                        onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                        placeholder="Full address"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="text-amber-300">GSTIN</Label>
                      <Input
                        value={vendorForm.gstin}
                        onChange={(e) => setVendorForm({ ...vendorForm, gstin: e.target.value.toUpperCase() })}
                        className="bg-black/50 border-amber-500/30 text-white mt-1 font-mono"
                        placeholder="e.g., 33AAACN2359J1ZJ"
                        maxLength={15}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddVendorOpen(false)} className="border-gray-500 text-gray-300">
                      Cancel
                    </Button>
                    <Button onClick={handleAddVendor} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                      Add Vendor
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search vendors..."
                  value={vendorSearch}
                  onChange={(e) => setVendorSearch(e.target.value)}
                  className="pl-10 bg-black/50 border-amber-500/30 text-white"
                />
              </div>

              {/* Vendors Table */}
              {loadingVendors ? (
                <div className="text-center py-8 text-gray-400">Loading vendors...</div>
              ) : filteredVendors.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No vendors added yet</p>
                  <p className="text-gray-500 text-sm">Click "Add Vendor" to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-amber-500/30 hover:bg-transparent">
                        <TableHead className="text-amber-300">Company Name</TableHead>
                        <TableHead className="text-amber-300">Contact Person</TableHead>
                        <TableHead className="text-amber-300">Email</TableHead>
                        <TableHead className="text-amber-300">Phone</TableHead>
                        <TableHead className="text-amber-300">GSTIN</TableHead>
                        <TableHead className="text-amber-300 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredVendors.map((vendor) => (
                        <TableRow key={vendor.id} className="border-amber-500/20 hover:bg-amber-500/10">
                          <TableCell className="font-bold text-white">{vendor.company_name}</TableCell>
                          <TableCell className="text-gray-300">{vendor.vendor_name || "-"}</TableCell>
                          <TableCell className="text-gray-300">{vendor.email || "-"}</TableCell>
                          <TableCell className="text-gray-300">{vendor.phone || "-"}</TableCell>
                          <TableCell className="font-mono text-gray-300">{vendor.gstin || "-"}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditVendor(vendor)}
                                className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedVendor(vendor);
                                  setDeleteVendorOpen(true);
                                }}
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Vendor Dialog */}
          <Dialog open={isEditVendorOpen} onOpenChange={setIsEditVendorOpen}>
            <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-amber-400 text-xl">Edit Vendor</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div>
                  <Label className="text-amber-300">Vendor Company Name *</Label>
                  <Input
                    value={vendorForm.company_name}
                    onChange={(e) => setVendorForm({ ...vendorForm, company_name: e.target.value })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-amber-300">Vendor Name (Optional)</Label>
                  <Input
                    value={vendorForm.vendor_name}
                    onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-amber-300">Email</Label>
                    <Input
                      type="email"
                      value={vendorForm.email}
                      onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                      className="bg-black/50 border-amber-500/30 text-white mt-1"
                      placeholder="vendor@example.com"
                    />
                  </div>
                  <div>
                    <Label className="text-amber-300">Phone / WhatsApp</Label>
                    <Input
                      value={vendorForm.phone}
                      onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                      className="bg-black/50 border-amber-500/30 text-white mt-1"
                      placeholder="+91 9876543210"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-amber-300">Address</Label>
                  <Textarea
                    value={vendorForm.address}
                    onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-amber-300">GSTIN</Label>
                  <Input
                    value={vendorForm.gstin}
                    onChange={(e) => setVendorForm({ ...vendorForm, gstin: e.target.value.toUpperCase() })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1 font-mono"
                    maxLength={15}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditVendorOpen(false)} className="border-gray-500 text-gray-300">
                  Cancel
                </Button>
                <Button onClick={handleEditVendor} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Vendor Dialog */}
          <AlertDialog open={deleteVendorOpen} onOpenChange={setDeleteVendorOpen}>
            <AlertDialogContent className="bg-gray-900 border-red-500/50">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-400">Delete Vendor</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-400">
                  Are you sure you want to delete "{selectedVendor?.company_name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-gray-500 text-gray-300">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteVendor} className="bg-red-500 hover:bg-red-600">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* ============== MILLS TAB ============== */}
        <TabsContent value="mills" className="space-y-4 mt-4">
          <Card className="bg-black/60 border-2 border-amber-500/30">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-amber-400 flex items-center gap-2">
                <Factory className="h-5 w-5" />
                Mills
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20" onClick={() => setIsBulkMillOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Bulk Add
                </Button>
                <Dialog open={isAddMillOpen} onOpenChange={setIsAddMillOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={resetMillForm}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Mill
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-amber-400 text-xl">Add New Mill</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div>
                        <Label className="text-amber-300">Mill Name *</Label>
                        <Input
                          value={millForm.name}
                          onChange={(e) => setMillForm({ ...millForm, name: e.target.value })}
                          className="bg-black/50 border-amber-500/30 text-white mt-1"
                          placeholder="e.g., ABC Paper Mill"
                        />
                      </div>
                      <div>
                        <Label className="text-amber-300">Address (Optional)</Label>
                        <Textarea
                          value={millForm.address}
                          onChange={(e) => setMillForm({ ...millForm, address: e.target.value })}
                          className="bg-black/50 border-amber-500/30 text-white mt-1"
                          placeholder="Mill address"
                          rows={2}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-amber-300">Contact Person</Label>
                          <Input
                            value={millForm.contact_person}
                            onChange={(e) => setMillForm({ ...millForm, contact_person: e.target.value })}
                            className="bg-black/50 border-amber-500/30 text-white mt-1"
                            placeholder="Contact name"
                          />
                        </div>
                        <div>
                          <Label className="text-amber-300">Phone</Label>
                          <Input
                            value={millForm.phone}
                            onChange={(e) => setMillForm({ ...millForm, phone: e.target.value })}
                            className="bg-black/50 border-amber-500/30 text-white mt-1"
                            placeholder="Phone number"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-amber-300">Email</Label>
                          <Input
                            type="email"
                            value={millForm.email}
                            onChange={(e) => setMillForm({ ...millForm, email: e.target.value })}
                            className="bg-black/50 border-amber-500/30 text-white mt-1"
                            placeholder="email@example.com"
                          />
                        </div>
                        <div>
                          <Label className="text-amber-300">GSTIN</Label>
                          <Input
                            value={millForm.gstin}
                            onChange={(e) => setMillForm({ ...millForm, gstin: e.target.value })}
                            className="bg-black/50 border-amber-500/30 text-white mt-1 font-mono"
                            placeholder="e.g., 22AAAAA0000A1Z5"
                          />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddMillOpen(false)} className="border-gray-500 text-gray-300">
                        Cancel
                      </Button>
                      <Button onClick={handleAddMill} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                        Add Mill
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search mills..."
                  value={millSearch}
                  onChange={(e) => setMillSearch(e.target.value)}
                  className="pl-10 bg-black/50 border-amber-500/30 text-white"
                />
              </div>

              {/* Mills Table */}
              {loadingMills ? (
                <div className="text-center py-8 text-gray-400">Loading mills...</div>
              ) : filteredMills.length === 0 ? (
                <div className="text-center py-12">
                  <Factory className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No mills added yet</p>
                  <p className="text-gray-500 text-sm">Click "Add Mill" to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-amber-500/30 hover:bg-transparent">
                        <TableHead className="text-amber-300">Mill Name</TableHead>
                        <TableHead className="text-amber-300">Contact Person</TableHead>
                        <TableHead className="text-amber-300">Phone</TableHead>
                        <TableHead className="text-amber-300">GSTIN</TableHead>
                        <TableHead className="text-amber-300 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMills.map((mill) => (
                        <TableRow key={mill.id} className="border-amber-500/20 hover:bg-amber-500/10">
                          <TableCell className="font-bold text-white">{mill.name}</TableCell>
                          <TableCell className="text-gray-300">{mill.contact_person || "-"}</TableCell>
                          <TableCell className="text-gray-300">{mill.phone || "-"}</TableCell>
                          <TableCell className="font-mono text-gray-300">{mill.gstin || "-"}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditMill(mill)}
                                className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedMill(mill);
                                  setDeleteMillOpen(true);
                                }}
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Mill Dialog */}
          <Dialog open={isEditMillOpen} onOpenChange={setIsEditMillOpen}>
            <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-amber-400 text-xl">Edit Mill</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div>
                  <Label className="text-amber-300">Mill Name *</Label>
                  <Input
                    value={millForm.name}
                    onChange={(e) => setMillForm({ ...millForm, name: e.target.value })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-amber-300">Address</Label>
                  <Textarea
                    value={millForm.address}
                    onChange={(e) => setMillForm({ ...millForm, address: e.target.value })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-amber-300">Contact Person</Label>
                    <Input
                      value={millForm.contact_person}
                      onChange={(e) => setMillForm({ ...millForm, contact_person: e.target.value })}
                      className="bg-black/50 border-amber-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-amber-300">Phone</Label>
                    <Input
                      value={millForm.phone}
                      onChange={(e) => setMillForm({ ...millForm, phone: e.target.value })}
                      className="bg-black/50 border-amber-500/30 text-white mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-amber-300">Email</Label>
                    <Input
                      type="email"
                      value={millForm.email}
                      onChange={(e) => setMillForm({ ...millForm, email: e.target.value })}
                      className="bg-black/50 border-amber-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-amber-300">GSTIN</Label>
                    <Input
                      value={millForm.gstin}
                      onChange={(e) => setMillForm({ ...millForm, gstin: e.target.value })}
                      className="bg-black/50 border-amber-500/30 text-white mt-1 font-mono"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditMillOpen(false)} className="border-gray-500 text-gray-300">
                  Cancel
                </Button>
                <Button onClick={handleEditMill} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Mill Confirmation */}
          <AlertDialog open={deleteMillOpen} onOpenChange={setDeleteMillOpen}>
            <AlertDialogContent className="bg-gray-900 border-red-500/50">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-400">Delete Mill</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-400">
                  Are you sure you want to delete "{selectedMill?.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-gray-500 text-gray-300">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteMill} className="bg-red-500 hover:bg-red-600">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* ============== PRODUCT TYPES TAB ============== */}
        <TabsContent value="product-types" className="space-y-4 mt-4">
          <Card className="bg-black/60 border-2 border-amber-500/30">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-amber-400 flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Product Types
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20" onClick={() => setIsBulkPTOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Bulk Add
                </Button>
                <Dialog open={isAddPTOpen} onOpenChange={setIsAddPTOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={resetPTForm}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Product Type
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-amber-400 text-xl">Add New Product Type</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div>
                      <Label className="text-amber-300">Product Type Name *</Label>
                      <Input
                        value={ptForm.name}
                        onChange={(e) => setPTForm({ ...ptForm, name: e.target.value })}
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                        placeholder="e.g., Adhesive, Tape, etc."
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_paper_type"
                        checked={ptForm.is_paper_type}
                        onChange={(e) => setPTForm({ ...ptForm, is_paper_type: e.target.checked })}
                        className="w-4 h-4 accent-amber-500"
                      />
                      <Label htmlFor="is_paper_type" className="text-amber-300 cursor-pointer">
                        This is a paper type (uses reel-specific fields)
                      </Label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddPTOpen(false)} className="border-gray-500 text-gray-300">
                      Cancel
                    </Button>
                    <Button onClick={handleAddPT} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                      Add Product Type
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-300 text-sm">
                  <strong>Note:</strong> Default paper types (Kraft Paper, Duplex Paper, etc.) are always available. 
                  Add custom product types here for non-paper items like adhesives, tapes, etc.
                </p>
              </div>

              {/* Default Paper Types */}
              <div className="mb-6">
                <h3 className="text-amber-400 font-bold mb-2">Default Paper Types (Always Available)</h3>
                <div className="flex flex-wrap gap-2">
                  {PAPER_TYPES.map((pt) => (
                    <span key={pt} className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm border border-amber-500/30">
                      {pt}
                    </span>
                  ))}
                </div>
              </div>

              {/* Custom Product Types Table */}
              <h3 className="text-amber-400 font-bold mb-2">Custom Product Types</h3>
              {loadingProductTypes ? (
                <div className="text-center py-8 text-gray-400">Loading product types...</div>
              ) : productTypes.length === 0 ? (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No custom product types added yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-amber-500/30 hover:bg-transparent">
                        <TableHead className="text-amber-300">Name</TableHead>
                        <TableHead className="text-amber-300">Type</TableHead>
                        <TableHead className="text-amber-300 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productTypes.map((pt) => (
                        <TableRow key={pt.id} className="border-amber-500/20 hover:bg-amber-500/10">
                          <TableCell className="font-bold text-white">{pt.name}</TableCell>
                          <TableCell>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs",
                              pt.is_paper_type 
                                ? "bg-amber-500/20 text-amber-400" 
                                : "bg-purple-500/20 text-purple-400"
                            )}>
                              {pt.is_paper_type ? "Paper Type" : "Non-Paper Item"}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditPT(pt)}
                                className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedPT(pt);
                                  setDeletePTOpen(true);
                                }}
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit PT Dialog */}
          <Dialog open={isEditPTOpen} onOpenChange={setIsEditPTOpen}>
            <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-amber-400 text-xl">Edit Product Type</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div>
                  <Label className="text-amber-300">Product Type Name *</Label>
                  <Input
                    value={ptForm.name}
                    onChange={(e) => setPTForm({ ...ptForm, name: e.target.value })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="edit_is_paper_type"
                    checked={ptForm.is_paper_type}
                    onChange={(e) => setPTForm({ ...ptForm, is_paper_type: e.target.checked })}
                    className="w-4 h-4 accent-amber-500"
                  />
                  <Label htmlFor="edit_is_paper_type" className="text-amber-300 cursor-pointer">
                    This is a paper type
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditPTOpen(false)} className="border-gray-500 text-gray-300">
                  Cancel
                </Button>
                <Button onClick={handleEditPT} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete PT Dialog */}
          <AlertDialog open={deletePTOpen} onOpenChange={setDeletePTOpen}>
            <AlertDialogContent className="bg-gray-900 border-red-500/50">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-400">Delete Product Type</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-400">
                  Are you sure you want to delete "{selectedPT?.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-gray-500 text-gray-300">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeletePT} className="bg-red-500 hover:bg-red-600">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* ============== HSN CODES TAB ============== */}
        <TabsContent value="hsn-codes" className="space-y-4 mt-4">
          <Card className="bg-black/60 border-2 border-amber-500/30">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-amber-400 flex items-center gap-2">
                <Hash className="h-5 w-5" />
                HSN Codes
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20" onClick={() => setIsBulkHSNOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Bulk Add
                </Button>
                <Dialog open={isAddHSNOpen} onOpenChange={setIsAddHSNOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={resetHSNForm}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add HSN Code
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-amber-400 text-xl">Add New HSN Code</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div>
                      <Label className="text-amber-300">HSN Code *</Label>
                      <Input
                        value={hsnForm.code}
                        onChange={(e) => setHSNForm({ ...hsnForm, code: e.target.value })}
                        className="bg-black/50 border-amber-500/30 text-white mt-1 font-mono"
                        placeholder="e.g., 48191010"
                      />
                    </div>
                    <div>
                      <Label className="text-amber-300">Description</Label>
                      <Textarea
                        value={hsnForm.description}
                        onChange={(e) => setHSNForm({ ...hsnForm, description: e.target.value })}
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                        placeholder="e.g., Cartons, boxes and cases of corrugated paper"
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddHSNOpen(false)} className="border-gray-500 text-gray-300">
                      Cancel
                    </Button>
                    <Button onClick={handleAddHSN} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                      Add HSN Code
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* HSN Codes Table */}
              {loadingHSN ? (
                <div className="text-center py-8 text-gray-400">Loading HSN codes...</div>
              ) : hsnCodes.length === 0 ? (
                <div className="text-center py-8">
                  <Hash className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No HSN codes added yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-amber-500/30 hover:bg-transparent">
                        <TableHead className="text-amber-300">HSN Code</TableHead>
                        <TableHead className="text-amber-300">Description</TableHead>
                        <TableHead className="text-amber-300 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hsnCodes.map((hsn) => (
                        <TableRow key={hsn.id} className="border-amber-500/20 hover:bg-amber-500/10">
                          <TableCell className="font-mono font-bold text-white">{hsn.code}</TableCell>
                          <TableCell className="text-gray-300">{hsn.description || "-"}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditHSN(hsn)}
                                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20 h-8 px-3"
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedHSN(hsn);
                                  setHsnToDelete(hsn);
                                  setDeleteHSNOpen(true);
                                }}
                                className="border-red-500/50 text-red-400 hover:bg-red-500/20 h-8 px-3"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit HSN Dialog */}
          <Dialog open={isEditHSNOpen} onOpenChange={setIsEditHSNOpen}>
            <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-amber-400 text-xl">Edit HSN Code</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div>
                  <Label className="text-amber-300">HSN Code *</Label>
                  <Input
                    value={hsnForm.code}
                    onChange={(e) => setHSNForm({ ...hsnForm, code: e.target.value })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-amber-300">Description</Label>
                  <Textarea
                    value={hsnForm.description}
                    onChange={(e) => setHSNForm({ ...hsnForm, description: e.target.value })}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditHSNOpen(false)} className="border-gray-500 text-gray-300">
                  Cancel
                </Button>
                <Button onClick={handleEditHSN} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete HSN Dialog */}
          <AlertDialog open={deleteHSNOpen} onOpenChange={(open) => {
            setDeleteHSNOpen(open);
            if (!open) {
              setHsnToDelete(null);
            }
          }}>
            <AlertDialogContent className="bg-gray-900 border-red-500/50">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-400">Delete HSN Code</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-400">
                  Are you sure you want to delete HSN code "{hsnToDelete?.code}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-gray-500 text-gray-300">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteHSN} className="bg-red-500 hover:bg-red-600">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>
      </Tabs>
      
      {/* Bulk Add Vendors Dialog */}
      <Dialog open={isBulkVendorOpen} onOpenChange={setIsBulkVendorOpen}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-xl">Bulk Add Dealers</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-amber-300">Enter dealer names (one per line)</Label>
            <textarea
              value={bulkVendorText}
              onChange={(e) => setBulkVendorText(e.target.value)}
              className="w-full h-40 mt-2 p-3 bg-black/50 border border-amber-500/30 rounded-md text-white font-mono text-sm"
              placeholder="Dealer Company Name 1&#10;Dealer Company Name 2&#10;Dealer Company Name 3"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkVendorOpen(false)} className="border-gray-500 text-gray-300">Cancel</Button>
            <Button onClick={handleBulkAddVendors} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Add All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Add Mills Dialog */}
      <Dialog open={isBulkMillOpen} onOpenChange={setIsBulkMillOpen}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-xl">Bulk Add Mills</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-amber-300">Enter mill names (one per line)</Label>
            <textarea
              value={bulkMillText}
              onChange={(e) => setBulkMillText(e.target.value)}
              className="w-full h-40 mt-2 p-3 bg-black/50 border border-amber-500/30 rounded-md text-white font-mono text-sm"
              placeholder="ABC Paper Mill&#10;XYZ Paper Mill&#10;Quality Paper Mill"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkMillOpen(false)} className="border-gray-500 text-gray-300">Cancel</Button>
            <Button onClick={handleBulkAddMills} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Add All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Add Product Types Dialog */}
      <Dialog open={isBulkPTOpen} onOpenChange={setIsBulkPTOpen}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-xl">Bulk Add Product Types</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-amber-300">Enter product types (one per line)</Label>
            <textarea
              value={bulkPTText}
              onChange={(e) => setBulkPTText(e.target.value)}
              className="w-full h-40 mt-2 p-3 bg-black/50 border border-amber-500/30 rounded-md text-white font-mono text-sm"
              placeholder="Product Type 1&#10;Product Type 2&#10;Product Type 3"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkPTOpen(false)} className="border-gray-500 text-gray-300">Cancel</Button>
            <Button onClick={handleBulkAddPT} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Add All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Add HSN Codes Dialog */}
      <Dialog open={isBulkHSNOpen} onOpenChange={setIsBulkHSNOpen}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-xl">Bulk Add HSN Codes</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-amber-300">Enter HSN codes (one per line, format: CODE - Description)</Label>
            <textarea
              value={bulkHSNText}
              onChange={(e) => setBulkHSNText(e.target.value)}
              className="w-full h-40 mt-2 p-3 bg-black/50 border border-amber-500/30 rounded-md text-white font-mono text-sm"
              placeholder="48191010 - Carton boxes&#10;48025610 - Paper and paperboard&#10;39232990 - Plastic products"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkHSNOpen(false)} className="border-gray-500 text-gray-300">Cancel</Button>
            <Button onClick={handleBulkAddHSN} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Add All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Add Materials Dialog */}
      <Dialog open={isBulkMaterialOpen} onOpenChange={setIsBulkMaterialOpen}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-xl">Bulk Add Materials</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-400 text-sm">Add multiple materials at once. Each row creates a new material entry.</p>
            
            {bulkMaterialEntries.map((entry, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-black/30 rounded-lg border border-amber-500/20">
                <div className="col-span-3">
                  <Label className="text-amber-300 text-xs">Product Type</Label>
                  <Select
                    value={entry.product_type}
                    onValueChange={(v) => updateBulkMaterialEntry(index, 'product_type', v)}
                  >
                    <SelectTrigger className="bg-black/50 border-amber-500/30 text-white mt-1 h-9">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-amber-500/30">
                      {allProductTypes.map((pt) => (
                        <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Label className="text-amber-300 text-xs">RM Name / Item Name *</Label>
                  <Input
                    value={entry.item_name}
                    onChange={(e) => updateBulkMaterialEntry(index, 'item_name', e.target.value)}
                    className="bg-black/50 border-amber-500/30 text-white mt-1 h-9"
                    placeholder="e.g., Kraft Paper 18x24"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-amber-300 text-xs">HSN Code</Label>
                  <Input
                    value={entry.hsn_code}
                    onChange={(e) => updateBulkMaterialEntry(index, 'hsn_code', e.target.value)}
                    className="bg-black/50 border-amber-500/30 text-white mt-1 h-9 font-mono"
                    placeholder="48191010"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-amber-300 text-xs">Min Stock</Label>
                  <Input
                    type="number"
                    value={entry.min_stock}
                    onChange={(e) => updateBulkMaterialEntry(index, 'min_stock', e.target.value)}
                    className="bg-black/50 border-amber-500/30 text-white mt-1 h-9"
                    placeholder="0"
                  />
                </div>
                <div className="col-span-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBulkMaterialRow(index)}
                    className="text-red-400 hover:text-red-300 h-9 w-9 p-0"
                    disabled={bulkMaterialEntries.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              onClick={addBulkMaterialRow}
              className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Another Row
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkMaterialOpen(false)} className="border-gray-500 text-gray-300">
              Cancel
            </Button>
            <Button onClick={handleBulkAddMaterials} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
              Add All Materials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreDatabase;
